/**
 * Life4Earth V2 — Admin Role Rhythms (Platform Catalog)
 *
 * DEV-1157 Phase 2b / DEV-1234 iter 1. Sysadmin-only authoring surface
 * for the platform canonical rhythm catalog.
 *
 * IA (locked DEV-1234 mockup session 2026-06-04):
 *   - Level 1: Horizontal pillar tab strip. Pillars with zero active
 *     role definitions are suppressed.
 *   - Level 2: Roles within the selected pillar, alphabetical.
 *     Optionally sub-grouped by role_category when count > 10.
 *   - Level 3: Role detail panel — shows the attached rhythm + tasks,
 *     OR an "Attach rhythm" placeholder when no rhythm is attached.
 *     Task display: Recurring Tasks (cadence-grouped) + Standing Duties
 *     two-section split. Click a task → per-task editor tray.
 *
 * Task editor (the tray) lands as a follow-on slice; this iteration
 * ships the catalog navigation + read-only role/rhythm rendering so
 * the surface is real and Skip can verify the hierarchy before we wire
 * the tray's create/edit/delete affordances.
 *
 * English only — system admin UI per CLAUDE.md.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  attachRhythmToRoleDefinition,
  cloneRoleDefinition,
  createRoleRhythm,
  fetchAdminCatalog,
  fetchRhythmDetails,
  retireRoleDefinition,
  updateRoleRhythm,
  updateRoleRhythmTask,
  type AdminCatalogPillar,
  type AdminCatalogRole,
  type AdminCatalogResponse,
  type RhythmDetailsResponse,
  type RhythmTaskDetail,
} from "../../api/roleRhythms";
import {
  attachRoleBundleToDefinition,
  fetchAllRoleBundlesAdmin,
  type RoleBundleResponse,
} from "../../api/roleBundles";
import AddRoleTray from "./AddRoleTray";
import BundlesTabContent from "./BundlesTabContent";
import TaskEditorTray from "./TaskEditorTray";
import styles from "./AdminRoleRhythms.module.css";

/**
 * DEV-1214 iter 1. Sentinel value for the "Bundles" pseudo-tab that sits
 * alongside the five pillar tabs in the top tab strip. When the active
 * scope is this sentinel, we render BundlesTabContent instead of the
 * pillar-scoped role list / detail split.
 *
 * Modeled as a string (rather than a discriminated union) so the existing
 * pillarType state shape stays unchanged — we just compare against the
 * sentinel before treating the value as a real pillar_type.
 */
const BUNDLES_TAB_SENTINEL = "__bundles__";

// Threshold above which a pillar's role list is sub-grouped by
// role_category (per DEV-1234 design lock Q2=b).
const SUBGROUP_THRESHOLD = 10;

/**
 * DEV-1234 follow-up (2026-06-04). Admin console is light-themed
 * regardless of the user's global theme preference (AdminShell sets
 * background #f4f5f7, hardcoded). The shared component CSS in this
 * page uses CSS custom properties (var(--text-primary), etc.) that
 * default to DARK-theme values from the main app. Without these
 * overrides, the cards render dark on the admin's light background
 * with no readable contrast (Skip 2026-06-04 screenshot).
 *
 * Mirrors the LIGHT_THEME_OVERRIDES pattern from AdminGeographies.tsx
 * with additions for --bg-surface / --bg-elevated / --color-success /
 * etc. that this page consumes but AdminGeographies doesn't.
 */
const LIGHT_THEME_OVERRIDES: React.CSSProperties = {
  // @ts-ignore — CSS custom properties are valid inline styles at runtime.
  "--text-primary": "#020617",
  "--text-secondary": "#334155",
  "--text-tertiary": "#475569",
  "--text-muted": "#64748b",
  "--bg-primary": "#ffffff",
  "--bg-secondary": "#f8fafc",
  "--bg-tertiary": "#ffffff",
  "--bg-surface": "#ffffff",
  "--bg-elevated": "rgba(2, 6, 23, 0.04)",
  "--bg-hover": "rgba(0, 0, 0, 0.03)",
  "--border-default": "rgba(2, 6, 23, 0.15)",
  "--accent-primary": "#2563eb",
  "--accent-text": "#1d4ed8",
  "--color-success": "#16a34a",
  "--color-error": "#dc2626",
  "--color-warning": "#d97706",
  "--info-text": "#1d4ed8",
} as React.CSSProperties;

export default function AdminRoleRhythms() {
  const [catalog, setCatalog] = useState<AdminCatalogResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Selection state
  const [pillarType, setPillarType] = useState<string | null>(null);
  const [selectedRoleGuid, setSelectedRoleGuid] = useState<string | null>(null);

  // Lazy-fetched rhythm details for the currently selected role.
  const [rhythmDetails, setRhythmDetails] = useState<RhythmDetailsResponse | null>(null);
  const [rhythmDetailsLoading, setRhythmDetailsLoading] = useState(false);

  // DEV-1234 iter 2 — task currently open in the per-task editor tray.
  // null when the tray is closed.
  const [editingTaskGuid, setEditingTaskGuid] = useState<string | null>(null);

  // DEV-1234 iter 3 — create-mode tray state. When non-null, the tray
  // is open in create mode with this seed. Mutually exclusive with
  // editingTaskGuid (only one tray at a time). The task_kind can be
  // flipped inside the tray via the segmented control; this is only
  // the initial value when the tray opens.
  const [creatingTaskSeed, setCreatingTaskSeed] = useState<
    | { rhythmGuid: string; initialTaskKind: "recurring_task" | "standing_duty" }
    | null
  >(null);

  // DEV-1234 iter 9 — Add Role tray state. When true, the tray is open
  // and pre-filled with the current pillar tab.
  const [addRoleOpen, setAddRoleOpen] = useState(false);

  // DEV-1214 iter 3 — Role Bundles list, used by RoleDetail's Bundle
  // picker to render available bundles + resolve the role's current
  // bundle to a label. Refreshed alongside the catalog so attaching a
  // role via the picker reflects immediately.
  const [bundles, setBundles] = useState<RoleBundleResponse[]>([]);

  const loadBundles = useCallback(async () => {
    try {
      const list = await fetchAllRoleBundlesAdmin();
      setBundles(list);
    } catch {
      // Failure is non-fatal — picker just shows empty options.
      setBundles([]);
    }
  }, []);

  useEffect(() => {
    void loadBundles();
  }, [loadBundles]);

  // ── Initial catalog fetch ──────────────────────────────────

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAdminCatalog();
      setCatalog(result);
      // Auto-select first pillar so the page lands on real content.
      if (result.pillars.length > 0 && !pillarType) {
        setPillarType(result.pillars[0].pillarType);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load catalog");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  // ── Rhythm details fetch on role selection ─────────────────

  const selectedRole = useMemo<AdminCatalogRole | null>(() => {
    if (!catalog || !selectedRoleGuid) return null;
    for (const pillar of catalog.pillars) {
      const found = pillar.roles.find((r) => r.definitionGuid === selectedRoleGuid);
      if (found) return found;
    }
    return null;
  }, [catalog, selectedRoleGuid]);

  // Reusable rhythm-details loader. Called by the selection effect AND
  // by the post-save handler when the task editor closes, so the role
  // pane immediately reflects the edited task metadata.
  const loadRhythmDetailsFor = useCallback(async (rhythmGuid: string) => {
    setRhythmDetailsLoading(true);
    try {
      const details = await fetchRhythmDetails(rhythmGuid);
      setRhythmDetails(details);
    } catch {
      setRhythmDetails(null);
    } finally {
      setRhythmDetailsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedRole || !selectedRole.rhythm) {
      setRhythmDetails(null);
      return;
    }
    const rhythmGuid = selectedRole.rhythm.rhythmGuid;
    let cancelled = false;
    setRhythmDetailsLoading(true);
    fetchRhythmDetails(rhythmGuid)
      .then((details) => {
        if (!cancelled) setRhythmDetails(details);
      })
      .catch(() => {
        if (!cancelled) setRhythmDetails(null);
      })
      .finally(() => {
        if (!cancelled) setRhythmDetailsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRole]);

  // Resolve the task currently open in the editor tray. Null when the
  // tray is closed OR the task is no longer present in the loaded
  // rhythm details (e.g., after a delete in a future iter).
  const editingTask = useMemo<RhythmTaskDetail | null>(() => {
    if (!editingTaskGuid || !rhythmDetails) return null;
    return rhythmDetails.tasks.find((t) => t.taskGuid === editingTaskGuid) ?? null;
  }, [editingTaskGuid, rhythmDetails]);

  const handleTaskClick = useCallback((task: RhythmTaskDetail) => {
    setCreatingTaskSeed(null);
    setEditingTaskGuid(task.taskGuid);
  }, []);

  // DEV-1234 iter 4 — when the selected role has no rhythm attached, the
  // first "+ Add task" click auto-creates a rhythm named "{Role Label}
  // Cadence", attaches it to the role definition, then opens the create-
  // task tray pointing at the new rhythm. From the admin's perspective
  // the role "just works" — they never need to think about the rhythm
  // container as a separate object.
  const [attachingRhythm, setAttachingRhythm] = useState(false);

  const handleAddTask = useCallback(
    async (initialTaskKind: "recurring_task" | "standing_duty" = "recurring_task") => {
      if (!selectedRole) return;
      setEditingTaskGuid(null);

      // Happy path: role already has a rhythm.
      if (selectedRole.rhythm?.rhythmGuid) {
        setCreatingTaskSeed({
          rhythmGuid: selectedRole.rhythm.rhythmGuid,
          initialTaskKind,
        });
        return;
      }

      // No rhythm yet — provision one. Name it after the role for clarity
      // in the rhythm authoring surfaces (Engagements RhythmDisplayPanel,
      // admin lists, audit log).
      if (attachingRhythm) return;
      setAttachingRhythm(true);
      setError(null);
      try {
        const newRhythm = await createRoleRhythm({
          name: `${selectedRole.label} Cadence`,
          description: null,
        });
        await attachRhythmToRoleDefinition(
          selectedRole.definitionGuid,
          newRhythm.rhythmGuid,
        );
        // Refresh the catalog so role.rhythm is populated for subsequent
        // interactions. The catalog refetch is awaited so the role-detail
        // panel re-renders with the new rhythm header before the tray opens.
        await loadCatalog();
        setCreatingTaskSeed({
          rhythmGuid: newRhythm.rhythmGuid,
          initialTaskKind,
        });
      } catch (err: any) {
        setError(err?.message || "Failed to create a rhythm for this role.");
      } finally {
        setAttachingRhythm(false);
      }
    },
    [selectedRole, attachingRhythm, loadCatalog],
  );

  // After any successful save (edit, create, or retire), re-fetch the
  // rhythm details so the task list reflects the new state. Also bumps
  // the catalog if a task count changed — for now we reload the
  // rhythm details only; the catalog's taskCount is stale but cheap to
  // accept on this surface since the next role click would refresh it.
  const handleTaskSaved = useCallback(() => {
    if (selectedRole?.rhythm?.rhythmGuid) {
      void loadRhythmDetailsFor(selectedRole.rhythm.rhythmGuid);
    }
    // Also refresh the catalog so the rhythm summary's taskCount stays
    // in sync after a create or retire — both change the count.
    void loadCatalog();
  }, [selectedRole, loadRhythmDetailsFor, loadCatalog]);

  // DEV-1234 iter 9 — after a new role is created, refresh the catalog
  // so the new row appears in the list, then auto-select it so the
  // admin can immediately edit it / add tasks.
  const handleRoleCreated = useCallback(
    async (newDefinitionGuid: string) => {
      await loadCatalog();
      setSelectedRoleGuid(newDefinitionGuid);
    },
    [loadCatalog],
  );

  // DEV-1234 iter 9 — after a role is retired, refresh the catalog and
  // clear the selection (the role disappears from the list).
  const handleRoleRetired = useCallback(async () => {
    setSelectedRoleGuid(null);
    setEditingTaskGuid(null);
    setCreatingTaskSeed(null);
    await loadCatalog();
  }, [loadCatalog]);

  // ── Derived collections ─────────────────────────────────────

  const activePillar = useMemo<AdminCatalogPillar | null>(() => {
    if (!catalog || !pillarType) return null;
    return catalog.pillars.find((p) => p.pillarType === pillarType) ?? null;
  }, [catalog, pillarType]);

  const filteredRoles = useMemo<AdminCatalogRole[]>(() => {
    if (!activePillar) return [];
    const q = searchTerm.trim().toLowerCase();
    if (!q) return activePillar.roles;
    return activePillar.roles.filter(
      (r) =>
        r.label.toLowerCase().includes(q)
        || (r.description ?? "").toLowerCase().includes(q)
        || r.roleCategory.toLowerCase().includes(q),
    );
  }, [activePillar, searchTerm]);

  // Sub-group by role_category when count > threshold. Returns either
  // a flat list (for small pillars) or grouped sections (for large ones).
  const groupedRoles = useMemo<
    { kind: "flat"; roles: AdminCatalogRole[] }
    | { kind: "grouped"; groups: { category: string; roles: AdminCatalogRole[] }[] }
  >(() => {
    if (filteredRoles.length === 0) {
      return { kind: "flat", roles: [] };
    }
    if (!activePillar || activePillar.roles.length <= SUBGROUP_THRESHOLD) {
      return { kind: "flat", roles: filteredRoles };
    }
    const byCat = new Map<string, AdminCatalogRole[]>();
    for (const r of filteredRoles) {
      const cat = r.roleCategory || "other";
      const list = byCat.get(cat) ?? [];
      list.push(r);
      byCat.set(cat, list);
    }
    const groups = Array.from(byCat.entries())
      .map(([category, roles]) => ({ category, roles }))
      .sort((a, b) => a.category.localeCompare(b.category));
    return { kind: "grouped", groups };
  }, [activePillar, filteredRoles]);

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className={styles.page} style={LIGHT_THEME_OVERRIDES}>
      <div className={styles.header}>
        <h1 className={styles.title}>Role Catalogue — Platform</h1>
        <p className={styles.subtitle}>
          Sysadmin-only authoring surface. Pillar tabs manage role
          definitions + their rhythms; the Bundles tab manages Role Bundles
          (named operational patterns that bundle roles for application by
          a community). Org-scoped authoring lives in Community Settings.
        </p>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error}{" "}
          <button type="button" className={styles.linkBtn} onClick={loadCatalog}>
            Retry
          </button>
        </div>
      )}

      {/* ─── Level 1: Role Bundles tab + pillar tabs (DEV-1214 iter 1) ─── */}
      {catalog && (
        <div className={styles.pillarTabs} role="tablist">
          {/* Role Bundles pseudo-tab — leftmost, per Skip 2026-06-04. Sibling
              to pillar tabs per design lock Q1 ("New tab alongside pillars"). */}
          <button
            type="button"
            role="tab"
            aria-selected={pillarType === BUNDLES_TAB_SENTINEL}
            className={
              pillarType === BUNDLES_TAB_SENTINEL
                ? `${styles.pillarTab} ${styles.pillarTabActive}`
                : styles.pillarTab
            }
            onClick={() => {
              setPillarType(BUNDLES_TAB_SENTINEL);
              setSelectedRoleGuid(null);
            }}
            title="Role Bundles — named operational patterns that bundle roles"
          >
            Role Bundles
          </button>
          {catalog.pillars.map((p) => (
            <button
              key={p.pillarType}
              type="button"
              role="tab"
              aria-selected={p.pillarType === pillarType}
              className={
                p.pillarType === pillarType
                  ? `${styles.pillarTab} ${styles.pillarTabActive}`
                  : styles.pillarTab
              }
              onClick={() => {
                setPillarType(p.pillarType);
                setSelectedRoleGuid(null);
              }}
            >
              {p.label}
              <span className={styles.pillarTabCount}>{p.activeRoleCount}</span>
            </button>
          ))}
        </div>
      )}

      {loading && !catalog && (
        <div className={styles.empty}>Loading catalog…</div>
      )}

      {/* DEV-1214 iter 1 — Bundles tab content (sibling of the pillar
          split layout). When the Bundles tab is active, we render the
          BundlesTabContent component and skip the pillar split entirely. */}
      {catalog && pillarType === BUNDLES_TAB_SENTINEL && (
        <BundlesTabContent />
      )}

      {catalog && pillarType !== BUNDLES_TAB_SENTINEL && (
        <div className={styles.splitLayout}>
          {/* ─── Level 2: role list ─── */}
          <div className={styles.listColumn}>
            <div className={styles.listToolbar}>
              <input
                type="search"
                className={styles.searchInput}
                placeholder={`Search ${activePillar?.label.toLowerCase() ?? ""} roles`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                type="button"
                className={styles.addRoleBtn}
                onClick={() => setAddRoleOpen(true)}
                disabled={!activePillar}
                title={
                  activePillar
                    ? `Add a new role to ${activePillar.label}`
                    : "Pick a pillar tab first"
                }
              >
                + Add role
              </button>
            </div>

            {filteredRoles.length === 0 && (
              <div className={styles.empty}>
                {activePillar && activePillar.roles.length === 0
                  ? "No active roles in this pillar."
                  : "No matches for that search."}
              </div>
            )}

            {groupedRoles.kind === "flat" && (
              <div className={styles.roleList}>
                {groupedRoles.roles.map((role) => (
                  <RoleRow
                    key={role.definitionGuid}
                    role={role}
                    selected={role.definitionGuid === selectedRoleGuid}
                    onClick={() => setSelectedRoleGuid(role.definitionGuid)}
                  />
                ))}
              </div>
            )}

            {groupedRoles.kind === "grouped" && (
              <div className={styles.roleList}>
                {groupedRoles.groups.map((g) => (
                  <div key={g.category} className={styles.roleGroup}>
                    <div className={styles.roleGroupHeader}>
                      {g.category}
                      <span className={styles.roleGroupCount}>{g.roles.length}</span>
                    </div>
                    {g.roles.map((role) => (
                      <RoleRow
                        key={role.definitionGuid}
                        role={role}
                        selected={role.definitionGuid === selectedRoleGuid}
                        onClick={() => setSelectedRoleGuid(role.definitionGuid)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ─── Level 3: role detail ─── */}
          <div className={styles.detailColumn}>
            {!selectedRole ? (
              <div className={styles.empty}>
                Select a role from the list to view its rhythm and tasks.
              </div>
            ) : (
              <RoleDetail
                role={selectedRole}
                rhythmDetails={rhythmDetails}
                loading={rhythmDetailsLoading}
                bundles={bundles}
                onTaskClick={handleTaskClick}
                onAddTask={handleAddTask}
                onRhythmUpdated={handleTaskSaved}
                onRoleRetired={handleRoleRetired}
                onRoleCloned={handleRoleCreated}
                onBundleChanged={async (newBundleGuid) => {
                  // Persist via the iter 2 attach endpoint, then refresh
                  // both the catalog (so role.roleBundleGuid + the role row
                  // tag are current) and the bundles list (so RoleCount
                  // is right if the user flips back to the Bundles tab).
                  try {
                    await attachRoleBundleToDefinition(
                      selectedRole.definitionGuid,
                      newBundleGuid,
                    );
                    await loadCatalog();
                    await loadBundles();
                  } catch (err: any) {
                    setError(err?.message || "Failed to update bundle.");
                  }
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* DEV-1234 iter 2 — per-task editor tray (edit mode) */}
      {editingTask && (
        <TaskEditorTray
          mode="edit"
          task={editingTask}
          sourceRolePillarType={activePillar?.pillarType}
          sourceRoleDefinitionGuid={selectedRole?.definitionGuid}
          open={true}
          onClose={() => setEditingTaskGuid(null)}
          onSaved={handleTaskSaved}
        />
      )}

      {/* DEV-1234 iter 3 — per-task editor tray (create mode) */}
      {creatingTaskSeed && (
        <TaskEditorTray
          mode="create"
          rhythmGuid={creatingTaskSeed.rhythmGuid}
          initialTaskKind={creatingTaskSeed.initialTaskKind}
          open={true}
          onClose={() => setCreatingTaskSeed(null)}
          onSaved={handleTaskSaved}
        />
      )}

      {/* DEV-1234 iter 9 — Add role tray */}
      {addRoleOpen && activePillar && (
        <AddRoleTray
          pillarType={activePillar.pillarType}
          pillarLabel={activePillar.label}
          open={true}
          onClose={() => setAddRoleOpen(false)}
          onCreated={(guid) => void handleRoleCreated(guid)}
        />
      )}
    </div>
  );
}

// ── Subcomponents ──────────────────────────────────────────

interface RoleRowProps {
  role: AdminCatalogRole;
  selected: boolean;
  onClick: () => void;
}

function RoleRow({ role, selected, onClick }: RoleRowProps) {
  return (
    <button
      type="button"
      className={
        selected ? `${styles.roleRow} ${styles.roleRowActive}` : styles.roleRow
      }
      onClick={onClick}
    >
      <div className={styles.roleRowMain}>
        <span className={styles.roleRowLabel}>{role.label}</span>
        <div className={styles.roleRowMeta}>
          {role.rhythm ? (
            <span className={styles.tagRhythm} title={`${role.rhythm.taskCount} task(s)`}>
              {role.rhythm.taskCount} task{role.rhythm.taskCount === 1 ? "" : "s"}
            </span>
          ) : (
            <span className={styles.tagNoRhythm}>No rhythm</span>
          )}
          {role.isSystemLocked && (
            <span className={styles.tagLocked} title="Platform-locked">
              🔒
            </span>
          )}
        </div>
      </div>
      {role.description && (
        <div className={styles.roleRowDesc}>{role.description}</div>
      )}
    </button>
  );
}

interface RoleDetailProps {
  role: AdminCatalogRole;
  rhythmDetails: RhythmDetailsResponse | null;
  loading: boolean;
  /** DEV-1214 iter 3 — all active Role Bundles, used by the Bundle picker. */
  bundles: RoleBundleResponse[];
  onTaskClick: (task: RhythmTaskDetail) => void;
  onAddTask: () => void;
  /** DEV-1234 iter 6 — fired after rename / description edit so the
   *  parent can refresh both the catalog summary and rhythm details. */
  onRhythmUpdated: () => void;
  /** DEV-1234 iter 9 — fired after role soft-retire so the parent can
   *  refresh the catalog and clear the selection. */
  onRoleRetired: () => void;
  /** DEV-1234 iter 10 — fired after a successful Clone Role with the
   *  new role's GUID so the parent can refresh + auto-select it. */
  onRoleCloned: (newDefinitionGuid: string) => void;
  /** DEV-1214 iter 3 — fired when the admin changes the role's bundle
   *  (or clears it). Parent persists via attachRoleBundleToDefinition. */
  onBundleChanged: (newBundleGuid: string | null) => void;
}

function RoleDetail({
  role,
  rhythmDetails,
  loading,
  bundles,
  onTaskClick,
  onAddTask,
  onRhythmUpdated,
  onRoleRetired,
  onRoleCloned,
  onBundleChanged,
}: RoleDetailProps) {
  // DEV-1234 iter 7 — detach rhythm state. Inline confirm pattern;
  // mirrors the retire-task affordance in TaskEditorTray.
  const [confirmingDetach, setConfirmingDetach] = React.useState(false);
  const [detaching, setDetaching] = React.useState(false);
  const [detachError, setDetachError] = React.useState<string | null>(null);

  // DEV-1234 iter 9 — retire role state.
  const [confirmingRetire, setConfirmingRetire] = React.useState(false);
  const [retiring, setRetiring] = React.useState(false);
  const [retireError, setRetireError] = React.useState<string | null>(null);

  // DEV-1234 iter 10 — clone role state.
  const [confirmingClone, setConfirmingClone] = React.useState(false);
  const [cloneNameDraft, setCloneNameDraft] = React.useState("");
  const [cloning, setCloning] = React.useState(false);
  const [cloneError, setCloneError] = React.useState<string | null>(null);

  // Reset confirm state when the selected role changes (e.g. user
  // clicks a different role while the confirm strip is open).
  React.useEffect(() => {
    setConfirmingDetach(false);
    setDetachError(null);
    setConfirmingRetire(false);
    setRetireError(null);
    setConfirmingClone(false);
    setCloneError(null);
    setCloneNameDraft(`${role.label} (copy)`);
  }, [role.definitionGuid, role.label]);

  const handleDetach = async () => {
    if (detaching) return;
    setDetaching(true);
    setDetachError(null);
    try {
      await attachRhythmToRoleDefinition(role.definitionGuid, null);
      setConfirmingDetach(false);
      onRhythmUpdated();
    } catch (err: any) {
      setDetachError(err?.message || "Failed to detach rhythm.");
    } finally {
      setDetaching(false);
    }
  };

  const handleRetireRole = async () => {
    if (retiring) return;
    setRetiring(true);
    setRetireError(null);
    try {
      await retireRoleDefinition(role.definitionGuid);
      onRoleRetired();
    } catch (err: any) {
      setRetireError(err?.message || "Failed to retire role.");
      setRetiring(false);
    }
    // Note: don't reset `retiring` on success — the parent will unmount
    // this component when it clears the selection. Trying to setState
    // after unmount triggers a React warning.
  };

  const handleCloneRole = async () => {
    if (cloning) return;
    const trimmed = cloneNameDraft.trim();
    if (!trimmed) {
      setCloneError("Clone name cannot be empty.");
      return;
    }
    setCloning(true);
    setCloneError(null);
    try {
      const created = await cloneRoleDefinition(role.definitionGuid, trimmed);
      onRoleCloned(created.definitionGuid);
      // Parent will re-render with the new selection; no need to reset
      // local clone state — this component unmounts shortly.
    } catch (err: any) {
      setCloneError(err?.message || "Failed to clone role.");
      setCloning(false);
    }
  };
  return (
    <div className={styles.detailPanel}>
      <div className={styles.detailHeader}>
        <h2 className={styles.detailTitle}>{role.label}</h2>
        <div className={styles.detailMeta}>
          <span className={styles.detailMetaTag}>{role.roleCategory}</span>
          {role.isSystemLocked && (
            <span className={styles.tagLocked} title="Platform-locked">
              🔒 Platform
            </span>
          )}
          {/* DEV-1234 iter 10 — Clone role affordance. Symmetric with
              Retire role; opens an inline strip with a name input. */}
          <button
            type="button"
            className={styles.cloneRoleBtn}
            onClick={() => {
              setCloneNameDraft(`${role.label} (copy)`);
              setConfirmingClone(true);
            }}
            disabled={cloning || confirmingClone}
            title="Clone this role (and its rhythm, tasks, and steps)"
          >
            Clone role
          </button>

          {/* DEV-1234 iter 9 — Retire role affordance. Subtle text-button
              styled like Detach rhythm; expands to an inline confirm
              strip on click. */}
          <button
            type="button"
            className={styles.retireRoleBtn}
            onClick={() => setConfirmingRetire(true)}
            disabled={retiring || confirmingRetire}
            title="Retire this role from the catalog"
          >
            Retire role
          </button>
        </div>
      </div>

      {role.description && (
        <p className={styles.detailDesc}>{role.description}</p>
      )}

      {/* DEV-1214 iter 3 — Bundle picker. Inline select; saves on change.
          Shows the role's current bundle (or "Not in any bundle"). The list
          of options is filtered to active bundles whose applicable_entity_types
          map to this role's pillar — bundles without entity_type restrictions
          show for any pillar. */}
      <RoleBundlePicker
        role={role}
        bundles={bundles}
        onChange={onBundleChanged}
      />

      {/* DEV-1234 iter 10 — inline clone confirm strip */}
      {confirmingClone && (
        <div className={styles.cloneRoleConfirm}>
          {cloneError && (
            <div className={styles.cloneRoleError}>{cloneError}</div>
          )}
          <p className={styles.cloneRoleConfirmText}>
            <strong>Clone {role.label}?</strong>{" "}
            A new role is created in the same pillar, copying the role
            metadata + (if attached) the rhythm + all tasks + all steps.
            Edit the name below before cloning.
          </p>
          <label className={styles.cloneRoleNameField}>
            <span>New role name</span>
            <input
              type="text"
              className={styles.cloneRoleNameInput}
              value={cloneNameDraft}
              onChange={(e) => setCloneNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleCloneRole();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setConfirmingClone(false);
                }
              }}
              disabled={cloning}
              maxLength={200}
              autoFocus
            />
          </label>
          <div className={styles.cloneRoleConfirmActions}>
            <button
              type="button"
              className={styles.cloneRoleCancelBtn}
              onClick={() => {
                setConfirmingClone(false);
                setCloneError(null);
              }}
              disabled={cloning}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.cloneRoleConfirmBtn}
              onClick={() => void handleCloneRole()}
              disabled={cloning || !cloneNameDraft.trim()}
            >
              {cloning ? "Cloning…" : "Clone role"}
            </button>
          </div>
        </div>
      )}

      {/* DEV-1234 iter 9 — inline retire confirm strip */}
      {confirmingRetire && (
        <div className={styles.retireRoleConfirm}>
          {retireError && (
            <div className={styles.retireRoleError}>{retireError}</div>
          )}
          <p className={styles.retireRoleConfirmText}>
            <strong>Retire {role.label}?</strong>{" "}
            The role is hidden from new slot pickers. Communities already
            using this role keep functioning — their slots and rhythm
            instances continue. The role and its attached rhythm aren't
            deleted; they can be reactivated later by editing
            <code> is_active </code> directly in the database.
          </p>
          <div className={styles.retireRoleConfirmActions}>
            <button
              type="button"
              className={styles.retireRoleCancelBtn}
              onClick={() => {
                setConfirmingRetire(false);
                setRetireError(null);
              }}
              disabled={retiring}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.retireRoleConfirmBtn}
              onClick={() => void handleRetireRole()}
              disabled={retiring}
            >
              {retiring ? "Retiring…" : "Retire role"}
            </button>
          </div>
        </div>
      )}

      {/* No rhythm attached → empty state + first-task CTA.
          Clicking + Add task auto-creates a rhythm named after the role
          and attaches it via DEV-1234 iter 4. */}
      {!role.rhythm && (
        <>
          <div className={styles.attachRhythmBox}>
            <p>
              <strong>No tasks yet.</strong>
            </p>
            <p>
              Adding the first task creates a rhythm named{" "}
              <em>{role.label} Cadence</em> and attaches it to this role.
              Every slot that uses this role will then receive the
              corresponding recurring tasks.
            </p>
          </div>
          <div className={styles.rhythmActions}>
            <button
              type="button"
              className={styles.topAddTaskBtn}
              onClick={() => onAddTask()}
            >
              + Add task
            </button>
          </div>
        </>
      )}

      {/* Rhythm attached → show editable header + task sections */}
      {role.rhythm && (
        <>
          <RhythmHeader
            rhythmGuid={role.rhythm.rhythmGuid}
            name={role.rhythm.name}
            description={rhythmDetails?.description ?? null}
            taskCount={role.rhythm.taskCount}
            isSystemLocked={role.rhythm.isSystemLocked}
            onUpdated={onRhythmUpdated}
          />


          {/* DEV-1234 iter 3 (revised) + iter 7 — rhythm actions row.
              Left: subtle Detach affordance. Right: green +Add task CTA. */}
          {role.rhythm && rhythmDetails && (
            <>
              <div className={styles.rhythmActions}>
                <button
                  type="button"
                  className={styles.detachRhythmBtn}
                  onClick={() => setConfirmingDetach(true)}
                  disabled={detaching || confirmingDetach}
                >
                  Detach rhythm
                </button>
                <button
                  type="button"
                  className={styles.topAddTaskBtn}
                  // Wrap in an arrow so React's MouseEvent doesn't become
                  // the first arg. handleAddTask's first param is
                  // initialTaskKind (defaults to "recurring_task"); a naked
                  // function ref leaked the SyntheticEvent in as the kind,
                  // which then flowed through state into the request body
                  // and crashed JSON.stringify with a circular-ref error
                  // ("HTMLButtonElement → __reactFiber → FiberNode →
                  // stateNode closes the circle"). DEV-1240.
                  onClick={() => onAddTask()}
                >
                  + Add task
                </button>
              </div>
              {confirmingDetach && (
                <div className={styles.detachConfirm}>
                  {detachError && (
                    <div className={styles.detachError}>{detachError}</div>
                  )}
                  <p className={styles.detachConfirmText}>
                    <strong>Detach this rhythm from {role.label}?</strong>{" "}
                    The rhythm and its tasks are not deleted — they just
                    stop being attached to this role. Communities already
                    using this role keep any open rhythm instances; new
                    role assignments will get no rhythm until one is
                    attached again.
                  </p>
                  <div className={styles.detachConfirmActions}>
                    <button
                      type="button"
                      className={styles.detachCancelBtn}
                      onClick={() => {
                        setConfirmingDetach(false);
                        setDetachError(null);
                      }}
                      disabled={detaching}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={styles.detachConfirmBtn}
                      onClick={() => void handleDetach()}
                      disabled={detaching}
                    >
                      {detaching ? "Detaching…" : "Detach rhythm"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {loading && !rhythmDetails && (
            <div className={styles.empty}>Loading tasks…</div>
          )}

          {rhythmDetails && (
            <TaskSections
              tasks={rhythmDetails.tasks}
              onTaskClick={onTaskClick}
              onReordered={onRhythmUpdated}
            />
          )}
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// RhythmHeader (DEV-1234 iter 6)
// ────────────────────────────────────────────────────────────
//
// Inline-editable rhythm name + description. Name is shown as a heading
// with a pencil button that reveals on hover. Click pencil (or click
// name) → input mode. Enter/blur saves; Escape cancels. Description is
// shown as italic muted text below the header row; click "+ Add
// description" placeholder when empty.

interface RhythmHeaderProps {
  rhythmGuid: string;
  name: string;
  description: string | null;
  taskCount: number;
  isSystemLocked: boolean;
  onUpdated: () => void;
}

function RhythmHeader({
  rhythmGuid,
  name,
  description,
  taskCount,
  isSystemLocked,
  onUpdated,
}: RhythmHeaderProps) {
  const [editingName, setEditingName] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState(name);
  const [savingName, setSavingName] = React.useState(false);

  const [editingDesc, setEditingDesc] = React.useState(false);
  const [descDraft, setDescDraft] = React.useState(description ?? "");
  const [savingDesc, setSavingDesc] = React.useState(false);

  const [error, setError] = React.useState<string | null>(null);

  // Reset drafts when the underlying rhythm changes (different role
  // selected, or after a successful save).
  React.useEffect(() => {
    if (!editingName) setNameDraft(name);
  }, [name, editingName]);
  React.useEffect(() => {
    if (!editingDesc) setDescDraft(description ?? "");
  }, [description, editingDesc]);

  const commitName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setError("Rhythm name cannot be empty.");
      setNameDraft(name);
      setEditingName(false);
      return;
    }
    if (trimmed === name) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    setError(null);
    try {
      await updateRoleRhythm(rhythmGuid, { name: trimmed });
      setEditingName(false);
      onUpdated();
    } catch (err: any) {
      setError(err?.message || "Failed to rename rhythm.");
      setNameDraft(name);
      setEditingName(false);
    } finally {
      setSavingName(false);
    }
  };

  const commitDescription = async () => {
    const trimmed = descDraft.trim();
    const current = description ?? "";
    if (trimmed === current) {
      setEditingDesc(false);
      return;
    }
    setSavingDesc(true);
    setError(null);
    try {
      // Empty string clears the description column (backend treats
      // whitespace-only as null per its standard patch shape).
      await updateRoleRhythm(rhythmGuid, { description: trimmed });
      setEditingDesc(false);
      onUpdated();
    } catch (err: any) {
      setError(err?.message || "Failed to save description.");
      setDescDraft(current);
      setEditingDesc(false);
    } finally {
      setSavingDesc(false);
    }
  };

  return (
    <div className={styles.rhythmHeaderBlock}>
      {error && (
        <div className={styles.rhythmHeaderError}>{error}</div>
      )}
      <div className={styles.rhythmHeader}>
        {editingName ? (
          <input
            type="text"
            className={styles.rhythmNameInput}
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void commitName();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setNameDraft(name);
                setEditingName(false);
              }
            }}
            onBlur={() => void commitName()}
            disabled={savingName}
            autoFocus
            maxLength={200}
          />
        ) : (
          <button
            type="button"
            className={styles.rhythmNameEditTrigger}
            onClick={() => setEditingName(true)}
            title="Click to rename"
          >
            <span className={styles.rhythmName}>{name}</span>
            <span className={styles.rhythmNamePencil} aria-hidden="true">✏️</span>
          </button>
        )}
        <span className={styles.rhythmMeta}>
          {taskCount} task{taskCount === 1 ? "" : "s"}
          {isSystemLocked && " · Platform-locked"}
        </span>
      </div>

      {/* Description row */}
      <div className={styles.rhythmDescriptionRow}>
        {editingDesc ? (
          <textarea
            className={styles.rhythmDescriptionInput}
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                setDescDraft(description ?? "");
                setEditingDesc(false);
              }
            }}
            onBlur={() => void commitDescription()}
            disabled={savingDesc}
            autoFocus
            rows={2}
            placeholder="Describe what this rhythm is for (optional)…"
          />
        ) : description ? (
          <button
            type="button"
            className={styles.rhythmDescriptionDisplay}
            onClick={() => setEditingDesc(true)}
            title="Click to edit description"
          >
            <span className={styles.rhythmDescriptionText}>{description}</span>
            <span className={styles.rhythmDescriptionPencil} aria-hidden="true">✏️</span>
          </button>
        ) : (
          <button
            type="button"
            className={styles.rhythmDescriptionAdd}
            onClick={() => setEditingDesc(true)}
          >
            + Add description
          </button>
        )}
      </div>
    </div>
  );
}

// Cadence ordering for the Recurring section sub-groups (mirrors
// RhythmDisplayPanel's CADENCE_ORDER).
const CADENCE_ORDER: string[] = [
  "daily",
  "weekly",
  "biweekly",
  "monthly_dom",
  "monthly_dow",
  "quarterly",
  "semiannual",
  "annual",
  "school_term",
  "fiscal_year_end",
];

const CADENCE_LABEL: Record<string, string> = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Biweekly",
  monthly_dom: "Monthly (by date)",
  monthly_dow: "Monthly (by weekday)",
  quarterly: "Quarterly",
  semiannual: "Semiannual",
  annual: "Annual",
  school_term: "School term",
  fiscal_year_end: "Fiscal year end",
};

function TaskSections({
  tasks,
  onTaskClick,
  onReordered,
}: {
  tasks: RhythmTaskDetail[];
  onTaskClick: (task: RhythmTaskDetail) => void;
  onReordered: () => void;
}) {
  // DEV-1234 iter 8 — drag-to-reorder state. Mirrors the step reorder
  // pattern from iter 5; scoped per group at drop time (cross-group
  // drags are no-ops).
  const [draggedTaskGuid, setDraggedTaskGuid] = React.useState<string | null>(null);
  const [dragOverTaskGuid, setDragOverTaskGuid] = React.useState<string | null>(null);
  const [reordering, setReordering] = React.useState(false);

  const recurring = tasks.filter((t) => t.taskKind === "recurring_task");
  const standing = tasks.filter((t) => t.taskKind === "standing_duty");

  // Returns the group key for a task — same key = drag-reorderable peers.
  // Recurring tasks: "cadence:<type>". Standing duties: "standing".
  const groupKey = (t: RhythmTaskDetail): string =>
    t.taskKind === "standing_duty" ? "standing" : `cadence:${t.cadenceType ?? "_unknown"}`;

  const handleDragStart = (taskGuid: string) => (e: React.DragEvent) => {
    setDraggedTaskGuid(taskGuid);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", taskGuid);
  };

  const handleDragOver = (taskGuid: string) => (e: React.DragEvent) => {
    // DEV-1234 iter 8 hotfix — only accept the drop if source and
    // target share a group (same cadence, or both Standing Duties).
    // Skipping preventDefault() here makes the browser show the
    // "not allowed" cursor and refuse the drop entirely, so the
    // user gets immediate visual feedback instead of being misled
    // by a blue drop-target highlight that does nothing on release.
    const source = tasks.find((t) => t.taskGuid === draggedTaskGuid);
    const target = tasks.find((t) => t.taskGuid === taskGuid);
    if (!source || !target || groupKey(source) !== groupKey(target)) {
      e.dataTransfer.dropEffect = "none";
      setDragOverTaskGuid(null);
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverTaskGuid(taskGuid);
  };

  const handleDragLeave = () => {
    setDragOverTaskGuid(null);
  };

  const handleDragEnd = () => {
    setDraggedTaskGuid(null);
    setDragOverTaskGuid(null);
  };

  const handleDrop = (targetTaskGuid: string) => async (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedTaskGuid(null);
    setDragOverTaskGuid(null);

    const sourceTaskGuid = e.dataTransfer.getData("text/plain");
    if (!sourceTaskGuid || sourceTaskGuid === targetTaskGuid) return;

    const source = tasks.find((t) => t.taskGuid === sourceTaskGuid);
    const target = tasks.find((t) => t.taskGuid === targetTaskGuid);
    if (!source || !target) return;

    // Cross-group drags are silently ignored. The cadence-group split
    // is the source of truth for "which tasks can sit next to each
    // other"; mixing them via drag would require changing cadence_type,
    // which is a metadata edit done in the tray.
    if (groupKey(source) !== groupKey(target)) return;

    const groupTasks = tasks
      .filter((t) => groupKey(t) === groupKey(source))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));

    const sourceIdx = groupTasks.findIndex((t) => t.taskGuid === source.taskGuid);
    const targetIdx = groupTasks.findIndex((t) => t.taskGuid === target.taskGuid);
    if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) return;

    const reordered = [...groupTasks];
    const [moved] = reordered.splice(sourceIdx, 1);
    reordered.splice(targetIdx, 0, moved);

    // PUT each task whose new index differs from its original sort_order.
    // We use idx as the new sort_order (0, 1, 2, ...) — sort order is
    // purely relative within a group so any monotonic sequence works.
    const updates = reordered
      .map((task, idx) => ({ task, newSortOrder: idx }))
      .filter(({ task, newSortOrder }) => task.sortOrder !== newSortOrder);

    if (updates.length === 0) return;

    setReordering(true);
    try {
      await Promise.all(
        updates.map(({ task, newSortOrder }) =>
          updateRoleRhythmTask(task.taskGuid, { sortOrder: newSortOrder }),
        ),
      );
      onReordered();
    } catch {
      // Failure: refresh anyway to pick up whatever partial state landed.
      onReordered();
    } finally {
      setReordering(false);
    }
  };

  const dragProps = (task: RhythmTaskDetail) => {
    const isDragged = draggedTaskGuid === task.taskGuid;
    const isDragOver =
      dragOverTaskGuid === task.taskGuid && draggedTaskGuid !== task.taskGuid;
    return {
      isDragged,
      isDragOver,
      onDragStart: handleDragStart(task.taskGuid),
      onDragOver: handleDragOver(task.taskGuid),
      onDragLeave: handleDragLeave,
      onDragEnd: handleDragEnd,
      onDrop: handleDrop(task.taskGuid),
      reordering,
    };
  };

  // Group recurring by cadence_type, ordered per CADENCE_ORDER.
  const byCadence = new Map<string, RhythmTaskDetail[]>();
  for (const t of recurring) {
    const key = t.cadenceType ?? "_unknown";
    const list = byCadence.get(key) ?? [];
    list.push(t);
    byCadence.set(key, list);
  }
  const orderedCadences: string[] = [];
  for (const c of CADENCE_ORDER) if (byCadence.has(c)) orderedCadences.push(c);
  for (const k of byCadence.keys()) {
    if (!orderedCadences.includes(k)) orderedCadences.push(k);
  }

  return (
    <div className={styles.taskSections}>
      <section className={styles.taskSection}>
        <div className={styles.taskSectionHeader}>
          Recurring Tasks
          <span className={styles.taskSectionCount}>{recurring.length}</span>
        </div>
        {recurring.length === 0 && (
          <div className={styles.empty}>No recurring tasks.</div>
        )}
        {orderedCadences.map((cadence) => {
          const list = (byCadence.get(cadence) ?? []).sort(
            (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
          );
          return (
            <div key={cadence} className={styles.cadenceGroup}>
              <div className={styles.cadenceGroupHeader}>
                {CADENCE_LABEL[cadence] ?? cadence}
                <span className={styles.cadenceGroupCount}>{list.length}</span>
              </div>
              {list.map((t) => (
                <TaskRow
                  key={t.taskGuid}
                  task={t}
                  onClick={() => onTaskClick(t)}
                  {...dragProps(t)}
                />
              ))}
            </div>
          );
        })}
      </section>

      <section className={styles.taskSection}>
        <div className={styles.taskSectionHeader}>
          Standing Duties
          <span className={styles.taskSectionCount}>{standing.length}</span>
        </div>
        {standing.length === 0 && (
          <div className={styles.empty}>No standing duties.</div>
        )}
        {standing
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
          .map((t) => (
            <TaskRow
              key={t.taskGuid}
              task={t}
              onClick={() => onTaskClick(t)}
              {...dragProps(t)}
            />
          ))}
      </section>
    </div>
  );
}

function TaskRow({
  task,
  onClick,
  isDragged,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDragEnd,
  onDrop,
  reordering,
}: {
  task: RhythmTaskDetail;
  onClick: () => void;
  isDragged: boolean;
  isDragOver: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent) => void;
  reordering: boolean;
}) {
  const rowClass = isDragged
    ? `${styles.taskRow} ${styles.taskRowDragging}`
    : isDragOver
    ? `${styles.taskRow} ${styles.taskRowDragOver}`
    : styles.taskRow;

  // DEV-1234 iter 8 — drag affordance lives on a handle, not the whole
  // row, so clicking the row to open the editor doesn't get confused
  // with a drag intent. Native HTML5 DnD; matches the step-row pattern.
  return (
    <div
      className={rowClass}
      draggable={!reordering}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
    >
      <span className={styles.taskDragHandle} aria-hidden="true" title="Drag to reorder">
        ⋮⋮
      </span>
      <button
        type="button"
        className={styles.taskRowClickArea}
        onClick={onClick}
      >
        <span className={styles.taskName}>{task.name}</span>
        <span className={styles.taskMeta}>
          {task.taskKind === "recurring_task" && task.leadTimeDays > 0
            ? `${task.leadTimeDays}d lead`
            : ""}
        </span>
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// RoleBundlePicker (DEV-1214 iter 3)
// ────────────────────────────────────────────────────────────
//
// Inline native <select> for assigning the role to a Role Bundle.
// Saves on change (no explicit save button — matches the inline-edit
// pattern of RhythmHeader's name / description fields). Defaults the
// options to bundles whose applicable_entity_types include any
// entity_type that maps to the role's pillar; a "Show all bundles"
// checkbox surfaces bundles outside that filter (symmetric with the
// "Show all pillars" override on AddRoleToBundleTray).

function RoleBundlePicker({
  role,
  bundles,
  onChange,
}: {
  role: AdminCatalogRole;
  bundles: RoleBundleResponse[];
  onChange: (newBundleGuid: string | null) => void;
}) {
  const [showAll, setShowAll] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  // Role pillar → entity_type pillar mapping (inverse of the one in
  // AddRoleToBundleTray). Used to detect whether a bundle's
  // applicable_entity_types target this role's pillar.
  const rolePillarToEntityPillar: Record<string, string> = {
    org_community: "org",
    geo_community: "geo",
    full_project:  "project",
    workspace:     "workspace",
  };

  const matchingEntityPillar = rolePillarToEntityPillar[role.pillarType];

  // Filter bundles: only active; default-filter to bundles that target
  // this role's pillar via at least one applicable_entity_type. The
  // entity_type → pillar mapping uses getEntityTypeMeta from the iter 2.5
  // catalog. Bundles with no entity_type restrictions show for any pillar.
  const visibleBundles = React.useMemo(() => {
    return bundles
      .filter((b) => b.isActive)
      .filter((b) => {
        if (showAll) return true;
        if (b.applicableEntityTypes.length === 0) return true; // no restriction
        if (!matchingEntityPillar) return true; // role pillar can't be mapped
        return b.applicableEntityTypes.some((et) => {
          // Lazy import via require pattern not great; inline the check
          // by string-prefix match instead (org_* → 'org', geo_* → 'geo').
          if (matchingEntityPillar === "org" && (et === "organization" || et.startsWith("org_"))) return true;
          if (matchingEntityPillar === "geo" && et.startsWith("geo_")) return true;
          if (matchingEntityPillar === "project" && et === "full_project") return true;
          if (matchingEntityPillar === "workspace" && et === "solo_creator_workshop") return true;
          return false;
        });
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [bundles, showAll, matchingEntityPillar]);

  // Current value handling: when role.roleBundleGuid points at a bundle
  // that's NOT in visibleBundles (e.g. retired, or filtered out by pillar),
  // we still want the <select> to render the current value as its
  // selected option — otherwise the dropdown would silently revert. So
  // we synthesize an "extra" option for the current bundle when needed.
  const currentBundle = role.roleBundleGuid
    ? bundles.find((b) => b.roleBundleGuid.toUpperCase() === role.roleBundleGuid!.toUpperCase()) ?? null
    : null;
  const currentInVisible = currentBundle
    ? visibleBundles.some((b) => b.roleBundleGuid.toUpperCase() === currentBundle.roleBundleGuid.toUpperCase())
    : true;

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value === "" ? null : e.target.value;
    setSaving(true);
    try {
      await onChange(next);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        marginBlockStart: 12,
        marginBlockEnd: 12,
        padding: "8px 12px",
        background: "var(--bg-elevated)",
        borderRadius: 4,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 8,
        fontSize: 12,
      }}
    >
      <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ color: "var(--text-secondary)", fontWeight: 500 }}>Bundle:</span>
        <select
          value={role.roleBundleGuid ?? ""}
          onChange={handleChange}
          disabled={saving}
          style={{
            padding: "4px 8px",
            background: "var(--bg-surface)",
            color: "var(--text-primary)",
            border: "1px solid var(--border-default)",
            borderRadius: 4,
            fontSize: 12,
            minWidth: 220,
          }}
        >
          <option value="">— Not in any bundle —</option>
          {currentBundle && !currentInVisible && (
            <option value={currentBundle.roleBundleGuid}>
              {currentBundle.label} (current, outside filter)
            </option>
          )}
          {visibleBundles.map((b) => (
            <option key={b.roleBundleGuid} value={b.roleBundleGuid}>
              {b.label}
              {b.applicableEntityTypes.length > 0 && ` — ${b.applicableEntityTypes.join(", ")}`}
            </option>
          ))}
        </select>
      </label>
      {matchingEntityPillar && (
        <label style={{ display: "flex", alignItems: "center", gap: 4, color: "var(--text-muted)" }}>
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            disabled={saving}
          />
          Show all bundles
        </label>
      )}
      {saving && (
        <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>Saving…</span>
      )}
    </div>
  );
}
